// src/features/records/components/CalendarView/index.js
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MonthView from './MonthView';
import ListView from './ListView';
import { navigateMonth, getCalendarGridRange } from './utils/dateHelpers';
import useRecordDetail from '../../hooks/useRecordDetail';
import useRecordList from '../../hooks/useRecordList';
import useFilterStore from '../../stores/useFilterStore';
import { useRecordsLayoutContext } from '../../hooks/useRecordsLayoutContext';
import { getIdentityColorSet } from '../../utils/identityColors';
import useUserProfiles from '../../hooks/useUserProfiles';
import mapRecordsErrorToKey from '../../utils/recordsErrorMapper';
import styles from './CalendarView.module.css';

const CalendarView = () => {
  const { t } = useTranslation();
  const { searchQuery } = useRecordsLayoutContext();
  const { openDetail } = useRecordDetail();
  const scope = useFilterStore((state) => state.scope);

  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const trimmedSearch = (searchQuery || '').trim();
  const searchActive = trimmedSearch.length > 0;

  const monthRange = useMemo(() => {
    if (searchActive) return null;
    return getCalendarGridRange(currentMonth, 0);
  }, [currentMonth, searchActive]);

  // Use Edge-Indexed list hook
  const { records, loading, error } = useRecordList({
    viewType: 'calendar',
    scope,
    sortSpec: { key: 'date', dir: 'asc' }, // Sort by date for calendar
    searchTerm: trimmedSearch,
    pageSize: 100,
    range: monthRange
      ? { key: 'date', start: monthRange.start, end: monthRange.end }
      : null
  });

  // Records are loaded via Edge-Indexed architecture with view-scoped subscription

  // When search is active, records are already server-filtered and ranked by Algolia.
  // No client-side filtering needed - trust the server results.
  const coloredRecords = useMemo(() => {
    if (!Array.isArray(records)) return records;

    return records.map((record) => {
      const ownerId = record?.access?.owner ?? record?.ownerId ?? '';
      const colorSeed = ownerId || record?.id || '__record__';
      const { accent, tint, tintStrong } = getIdentityColorSet(colorSeed);

      const override = {};

      if (record.calendarColor !== accent) {
        override.calendarColor = accent;
      }

      if (record.calendarColorTint !== tint) {
        override.calendarColorTint = tint;
      }

      if (record.calendarColorTintHover !== tintStrong) {
        override.calendarColorTintHover = tintStrong;
      }

      if (!record.categoryColor) {
        override.categoryColor = accent;
      }

      return Object.keys(override).length ? { ...record, ...override } : record;
    });
  }, [records]);

  const filteredRecords = coloredRecords;
  const ownerIds = useMemo(() => {
    if (!Array.isArray(filteredRecords) || filteredRecords.length === 0) {
      return [];
    }

    const ids = new Set();
    filteredRecords.forEach((record) => {
      const id = record?.access?.owner ?? record?.ownerId ?? null;
      if (id) {
        ids.add(id);
      }
    });

    return Array.from(ids);
  }, [filteredRecords]);

  const { profiles: userProfiles, loading: profilesLoading } = useUserProfiles(ownerIds);
  const calendarErrorKey = error
    ? mapRecordsErrorToKey(error, { fallbackKey: 'records.views.calendar.errors.load_failed' })
    : null;
  
  // Navigation handlers
  const handleMonthChange = useCallback((direction) => {
    setCurrentMonth(prev => navigateMonth(prev, direction));
  }, []);
  
  const handleTodayClick = useCallback(() => {
    setCurrentMonth(new Date());
  }, []);
  
  const handleDayClick = useCallback((date) => {
    setSelectedDate(date);
  }, []);
  
  const handleCloseListView = useCallback(() => {
    setSelectedDate(null);
  }, []);
  
  const handleRecordClick = useCallback((record) => {
    // Find the full record object if needed
    const fullRecord = coloredRecords.find(r => r.id === record.id) || record;
    openDetail(fullRecord);
  }, [coloredRecords, openDetail]);
  
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorMessage}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p>{t(calendarErrorKey || 'records.views.calendar.errors.load_failed')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.calendarView}>
      {selectedDate ? (
        <ListView
          selectedDate={selectedDate}
          records={filteredRecords}
          onClose={handleCloseListView}
          onRecordClick={handleRecordClick}
          loading={loading}
          userProfiles={userProfiles}
          profilesLoading={profilesLoading}
        />
      ) : (
        <>
          <MonthView
            records={filteredRecords}
            userProfiles={userProfiles}
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
            onTodayClick={handleTodayClick}
            onDayClick={handleDayClick}
            onRecordClick={handleRecordClick}
            loading={loading}
            profilesLoading={profilesLoading}
          />
          
          {/* Empty state */}
          {!loading && filteredRecords.length === 0 && (
            <div className={styles.emptyState}>
              <svg className={styles.emptyIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <p className={styles.emptyText}>
                {searchActive
                  ? t('records.views.calendar.empty.search')
                  : t('records.views.calendar.empty.month')}
              </p>
              <p className={styles.emptySubtext}>
                {t('records.views.calendar.empty.description')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CalendarView;
